import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useShare from '@/src/Share/components/hooks/useShare';
import useProduction from '@/src/Production/components/hooks/useProduction';
import useDeviceTelemetry from '@/src/DeviceTelemetry/hooks/useDeviceTelemetry';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import Sparkline from '../components/Sparkline';
import { eventDay, shareRate, photosUploaded } from '../lib/eventStats';
import { interactiveRow } from '../components/a11y';

/*
 * Home v2 — the command center (design: docs/design-system/mocks/
 * nav-redesign/). Live activations lead: a card grid when 2+ are live,
 * one large command card when exactly 1 is. Up Next and Pipeline · Jobs
 * fill the rest. The greeting+weather sentence stays (earlier decision;
 * the handoff mock omits it). Live detection rides the org latest-media
 * feed; per-event stats come from sessions/shares/queue. Dev-only
 * ?sim=N fakes N live events to review the busy grid.
 */

const LIVE_WINDOW_MIN = 45;
const FEED_POLL_MS = 30 * 1000;
const STATS_POLL_MS = 60 * 1000;
const SERIES_HOURS = 8;

interface FeedItem {
    id: number;
    activationId: number;
    activationName: string;
    thumbnailUri?: string;
    thumbnailTinyUri?: string;
    createdAt: string;
    mediaCount?: number;
}

interface ActivationRow {
    id: number;
    name: string;
    date: string;
    location?: string;
    owner?: string;
    isArchived: boolean;
}

interface LiveDetail {
    photos: number;
    sessions: number;
    shares: number;
    rate: number | null;
    series: number[];
    firstToday: string | null;
    devOnline: number;
    devTotal: number;
}

const WEATHER_CODES: Record<number, string> = {
    0: 'clear',
    1: 'mostly clear',
    2: 'partly cloudy',
    3: 'overcast',
    45: 'fog',
    48: 'fog',
    51: 'drizzle',
    53: 'drizzle',
    55: 'drizzle',
    61: 'light rain',
    63: 'rain',
    65: 'heavy rain',
    66: 'freezing rain',
    67: 'freezing rain',
    71: 'light snow',
    73: 'snow',
    75: 'heavy snow',
    77: 'snow',
    80: 'showers',
    81: 'showers',
    82: 'heavy showers',
    85: 'snow showers',
    86: 'snow showers',
    95: 'thunderstorms',
    96: 'thunderstorms',
    99: 'thunderstorms',
};

function compass(deg: number) {
    const dirs = [
        'north',
        'northeast',
        'east',
        'southeast',
        'south',
        'southwest',
        'west',
        'northwest',
    ];
    return dirs[Math.round(deg / 45) % 8];
}

function greetingWord(h: number) {
    if (h < 5) return 'Working late';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}

function elapsedLabel(since: string | null) {
    if (!since) return null;
    const mins = Math.max(0, moment().diff(moment(since), 'minutes'));
    return `${Math.floor(mins / 60)}H ${String(mins % 60).padStart(2, '0')}M`;
}

function dayLabel(d: moment.Moment) {
    if (d.isSame(moment(), 'day')) return 'TODAY';
    if (d.isSame(moment().add(1, 'day'), 'day')) return 'TOMORROW';
    return d.format('MMM D').toUpperCase();
}

const IconPlay = ({ size = 16 }: { size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
    >
        <path d="M9 6.5v11l9-5.5z" />
    </svg>
);

const IconImage = () => (
    <svg
        width={18}
        height={18}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
        <circle cx="9" cy="10" r="1.6" />
        <path d="M3.5 17l5-4.5 3.5 3 3.5-3 5 4.5" />
    </svg>
);

const IconSliders = () => (
    <svg
        width={18}
        height={18}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
    >
        <path d="M4 8h9M17 8h3M4 16h3M11 16h9" />
        <circle cx="15" cy="8" r="2" />
        <circle cx="9" cy="16" r="2" />
    </svg>
);

/* dev-only fake live events so the busy grid can be reviewed (?sim=N) */
function simDetails(n: number): Array<{
    id: number;
    name: string;
    venue: string;
    detail: LiveDetail;
}> {
    const names = [
        ['Spotify House ATX', 'Rainey St, Austin TX'],
        ['Fenty Beauty Bar', 'The Grove, Los Angeles CA'],
        ['Red Bull Campus Clash', 'UCLA, Los Angeles CA'],
        ['Lego Build Live', 'Mall of America, MN'],
        ['Bumble Hive Miami', 'Wynwood, Miami FL'],
        ['Patagonia Trail Day', 'Marymoor Park, Redmond WA'],
        ['A24 Screening Lounge', 'Alamo South, Austin TX'],
        ['Glossier You Tour', 'Georgetown, Washington DC'],
        ['Chewy Pet Fest', 'Zilker Park, Austin TX'],
        ['Vans Warped Booth', 'Pier 17, New York NY'],
        ['Athletic Brewing 5K', 'Golden Gate Park, SF'],
        ['Sonos Listening Room', 'SoDo, Seattle WA'],
        ['Everybody’s Somebody Fest', 'Fair Park, Dallas TX'],
    ];
    return Array.from({ length: Math.min(n, names.length) }, (_, i) => {
        const devTotal = 2 + (i % 5);
        return {
            id: -1000 - i,
            name: names[i][0],
            venue: names[i][1],
            detail: {
                photos: 90 + i * 217,
                sessions: 40 + i * 60,
                shares: 30 + i * 41,
                rate: 40 + (i % 5) * 9,
                series: Array.from(
                    { length: SERIES_HOURS },
                    (_, h) => ((i + 1) * (h + 3) * 37) % 90,
                ),
                firstToday: moment()
                    .subtract(40 + i * 27, 'minutes')
                    .toISOString(),
                devOnline: devTotal - (i % 4 === 3 ? 1 : 0),
                devTotal,
            },
        };
    });
}

export default function HomeV2() {
    const navigate = useNavigate();
    const authenticatedApi = useAuthenticatedApi();
    const [searchParams] = useSearchParams();
    const { orgId } = useUserOrg();
    const { getActivations } = useActivations();
    const { getActivationSessions, getActivationPhotos } = useActivation();
    const { getSharesForActivation } = useShare();
    const { getJobs } = useProduction();
    const { getQueueDataForActivation } = useDeviceTelemetry();

    const [now, setNow] = useState(() => moment());
    const [weather, setWeather] = useState<string | null>(null);
    const [feed, setFeed] = useState<FeedItem[]>([]);
    const [activations, setActivations] = useState<ActivationRow[]>([]);
    const [details, setDetails] = useState<Record<number, LiveDetail>>({});
    const [quietStrip, setQuietStrip] = useState<
        Array<{ id: number; uri: string }>
    >([]);
    const [quietPhotos, setQuietPhotos] = useState<number | null>(null);
    const [jobs, setJobs] = useState<any[]>([]);
    const [forecasts, setForecasts] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);
    const forecastCache = useRef<Record<string, string | null>>({});

    const simCount =
        process.env.NODE_ENV === 'development'
            ? parseInt(searchParams.get('sim') ?? '', 10) || 0
            : 0;

    /* clock ticks so the greeting stays honest */
    useEffect(() => {
        const timer = setInterval(() => setNow(moment()), 30 * 1000);
        return () => clearInterval(timer);
    }, []);

    /* local weather for the greeting — silent on any failure */
    useEffect(() => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const r = await fetch(
                        `https://api.open-meteo.com/v1/forecast?latitude=${pos.coords.latitude}&longitude=${pos.coords.longitude}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,wind_direction_10m&temperature_unit=fahrenheit&wind_speed_unit=mph`,
                    );
                    const data = await r.json();
                    const c = data?.current;
                    if (!c) return;
                    setWeather(
                        `${Math.round(c.temperature_2m)}°F, ${Math.round(
                            c.relative_humidity_2m,
                        )}% RH, wind ${Math.round(
                            c.wind_speed_10m,
                        )} mph out of the ${compass(c.wind_direction_10m)}.`,
                    );
                } catch {
                    /* no weather line */
                }
            },
            () => {},
            { maximumAge: 30 * 60 * 1000, timeout: 8000 },
        );
    }, []);

    /* org media feed — live detection + quiet-card freshness, polls */
    useEffect(() => {
        if (!orgId) return;
        let mounted = true;
        const load = async () => {
            try {
                const r = await authenticatedApi.get(`${orgId}/media/latest?`);
                if (mounted) setFeed(r.data ?? []);
            } catch {
                /* feed is ambient — keep the page usable without it */
            }
        };
        load();
        const timer = setInterval(load, FEED_POLL_MS);
        return () => {
            mounted = false;
            clearInterval(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    useEffect(() => {
        let mounted = true;
        getActivations()
            .then((rows: ActivationRow[]) => {
                if (mounted) setActivations(rows ?? []);
            })
            .catch(() => {})
            .finally(() => mounted && setIsLoading(false));
        getJobs()
            .then((rows: any[]) => {
                if (mounted) setJobs(rows ?? []);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const actById = useMemo(() => {
        const m = new Map<number, ActivationRow>();
        activations.forEach((a) => m.set(a.id, a));
        return m;
    }, [activations]);

    const liveEvents = useMemo(
        () =>
            feed.filter(
                (f) =>
                    moment().diff(moment(f.createdAt), 'minutes') <
                    LIVE_WINDOW_MIN,
            ),
        [feed],
    );

    const liveIdsKey = liveEvents.map((f) => f.activationId).join(',');

    /* per-live-event detail: photos, shares, hourly series, devices —
       rarely more than 2–3 events; polls quietly */
    useEffect(() => {
        if (!liveIdsKey || !orgId) return;
        let mounted = true;
        const ids = liveIdsKey.split(',').map(Number);
        const load = async () => {
            const entries = await Promise.all(
                ids.map(async (id) => {
                    try {
                        const [sessions, shares, queue] = await Promise.all([
                            getActivationSessions(id),
                            getSharesForActivation(id),
                            getQueueDataForActivation(orgId, id),
                        ]);
                        const seen = new Set();
                        const uniq = (sessions ?? []).filter((s: any) => {
                            const k = s.photoSessionId ?? s.id;
                            if (seen.has(k)) return false;
                            seen.add(k);
                            return true;
                        });
                        const seenSh = new Set();
                        const uniqShares = (shares ?? []).filter((s: any) => {
                            if (seenSh.has(s.id)) return false;
                            seenSh.add(s.id);
                            return true;
                        });
                        const photos = uniq.reduce(
                            (sum: number, s: any) =>
                                sum + (Number(s.total) || 0),
                            0,
                        );
                        /* hourly buckets, oldest → newest */
                        const buckets = new Array(SERIES_HOURS).fill(0);
                        const start = moment()
                            .subtract(SERIES_HOURS - 1, 'hours')
                            .startOf('hour');
                        let firstToday: string | null = null;
                        uniq.forEach((s: any) => {
                            const t = moment(s.createdAt ?? s.date);
                            if (!t.isValid()) return;
                            if (
                                t.isSame(moment(), 'day') &&
                                (!firstToday || t.isBefore(firstToday))
                            ) {
                                firstToday = t.toISOString();
                            }
                            const idx = t.diff(start, 'hours');
                            if (idx >= 0 && idx < SERIES_HOURS) {
                                buckets[idx] += Number(s.total) || 0;
                            }
                        });
                        const online = (queue ?? []).filter(
                            (q: any) =>
                                q.updatedAt &&
                                moment().diff(
                                    moment(q.updatedAt),
                                    'minutes',
                                ) < 10,
                        ).length;
                        return [
                            id,
                            {
                                photos,
                                sessions: uniq.length,
                                shares: uniqShares.length,
                                rate: shareRate(uniq, uniqShares),
                                series: buckets,
                                firstToday,
                                devOnline: online,
                                devTotal: (queue ?? []).length,
                            },
                        ] as const;
                    } catch {
                        return [
                            id,
                            {
                                photos: 0,
                                sessions: 0,
                                shares: 0,
                                rate: null,
                                series: new Array(SERIES_HOURS).fill(0),
                                firstToday: null,
                                devOnline: 0,
                                devTotal: 0,
                            },
                        ] as const;
                    }
                }),
            );
            if (mounted) setDetails(Object.fromEntries(entries));
        };
        load();
        const timer = setInterval(load, STATS_POLL_MS);
        return () => {
            mounted = false;
            clearInterval(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveIdsKey, orgId]);

    /* quiet state: the one live event's newest photos, native ratios */
    const quietId = liveEvents.length === 1 ? liveEvents[0].activationId : 0;
    useEffect(() => {
        if (!quietId) {
            setQuietStrip([]);
            setQuietPhotos(null);
            return;
        }
        let mounted = true;
        const load = async () => {
            const rows =
                (await getActivationPhotos(quietId, []).catch(() => [])) ?? [];
            if (!mounted) return;
            setQuietPhotos(photosUploaded(rows));
            setQuietStrip(
                rows
                    .filter((r: any) => !r.isHidden)
                    .sort(
                        (a: any, b: any) =>
                            moment(b.createdAt).valueOf() -
                            moment(a.createdAt).valueOf(),
                    )
                    .slice(0, 7)
                    .map((r: any) => ({
                        id: r.id,
                        uri:
                            r.thumbnailUri ??
                            r.thumbnail_800pxUri ??
                            r.originalUri,
                    }))
                    .filter((r: any) => r.uri),
            );
        };
        load();
        const timer = setInterval(load, FEED_POLL_MS);
        return () => {
            mounted = false;
            clearInterval(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quietId]);

    const upcoming = useMemo(() => {
        const liveIds = new Set(liveEvents.map((f) => f.activationId));
        return activations
            .filter(
                (a) =>
                    !a.isArchived &&
                    !liveIds.has(a.id) &&
                    eventDay(a.date).isSameOrAfter(moment().startOf('day')),
            )
            .sort(
                (a, b) =>
                    eventDay(a.date).valueOf() -
                    eventDay(b.date).valueOf(),
            )
            .slice(0, 5);
    }, [activations, liveEvents]);

    /* venue forecast per upcoming event — cache per location+date */
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            for (const a of upcoming) {
                const date = eventDay(a.date);
                if (
                    !a.location ||
                    date.diff(moment(), 'days') > 15 ||
                    !date.isValid()
                )
                    continue;
                const key = `${a.location}|${date.format('YYYY-MM-DD')}`;
                if (key in forecastCache.current) continue;
                forecastCache.current[key] = null;
                try {
                    const parts = a.location.split(',').map((p) => p.trim());
                    const candidates = [parts[parts.length - 1], parts[0]];
                    let geo: any = null;
                    for (const q of candidates) {
                        if (!q) continue;
                        const r = await fetch(
                            `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
                                q,
                            )}&count=1`,
                        );
                        const data = await r.json();
                        if (data?.results?.[0]) {
                            geo = data.results[0];
                            break;
                        }
                    }
                    if (!geo) continue;
                    const day = date.format('YYYY-MM-DD');
                    const r = await fetch(
                        `https://api.open-meteo.com/v1/forecast?latitude=${geo.latitude}&longitude=${geo.longitude}&daily=temperature_2m_max,weather_code&temperature_unit=fahrenheit&timezone=auto&start_date=${day}&end_date=${day}`,
                    );
                    const data = await r.json();
                    const t = data?.daily?.temperature_2m_max?.[0];
                    const code = data?.daily?.weather_code?.[0];
                    if (t == null) continue;
                    const label = `${Math.round(t)}°F${
                        WEATHER_CODES[code] ? `, ${WEATHER_CODES[code]}` : ''
                    }`;
                    forecastCache.current[key] = label;
                    if (mounted)
                        setForecasts((f) => ({ ...f, [key]: label }));
                } catch {
                    /* no forecast for this row */
                }
            }
        };
        load();
        return () => {
            mounted = false;
        };
    }, [upcoming]);

    /* pipeline: open jobs (any status outside past/wrapped/lost) */
    const pipeline = useMemo(() => {
        const stagePriority = [
            'in_production',
            'show_ready',
            'ready_for_production',
            'pending',
        ];
        const stageLabel: Record<string, string> = {
            in_production: 'IN PRODUCTION',
            show_ready: 'SHOW READY',
            ready_for_production: 'BOOKED',
            pending: 'INQUIRY',
        };
        return (jobs ?? [])
            .filter((j: any) => j.jobId != null)
            .map((j: any) => {
                const stage = stagePriority.find(
                    (s) => (j.statusCounts ?? {})[s] > 0,
                );
                return { ...j, stage };
            })
            .filter((j: any) => j.stage)
            .sort(
                (a: any, b: any) =>
                    moment(a.startAt ?? 0).valueOf() -
                    moment(b.startAt ?? 0).valueOf(),
            )
            .slice(0, 6)
            .map((j: any) => ({
                jobId: +j.jobId,
                name: j.name,
                meta: [
                    j.billingEntity,
                    `${j.activationCount} stop${
                        +j.activationCount === 1 ? '' : 's'
                    }`,
                    j.startAt && moment(j.startAt).isSameOrAfter(moment(), 'day')
                        ? `next: ${moment(j.startAt).format('MMM D')}`
                        : null,
                ]
                    .filter(Boolean)
                    .join(' · '),
                stage: stageLabel[j.stage] ?? j.stage,
            }));
    }, [jobs]);

    const greeting = `${greetingWord(now.hour())} — it's ${now.format(
        'h:mm A',
    )} on ${now.format('dddd, MMM D')}.`;

    /* live cards: real data, or the dev sim */
    const liveCards = useMemo(() => {
        if (simCount >= 2) return simDetails(simCount);
        return liveEvents.map((f) => {
            const a = actById.get(f.activationId);
            return {
                id: f.activationId,
                name: f.activationName || a?.name || `#${f.activationId}`,
                venue: a?.location ?? '',
                detail:
                    details[f.activationId] ??
                    ({
                        photos: 0,
                        sessions: 0,
                        shares: 0,
                        rate: null,
                        series: new Array(SERIES_HOURS).fill(0),
                        firstToday: f.createdAt,
                        devOnline: 0,
                        devTotal: 0,
                    } as LiveDetail),
            };
        });
    }, [simCount, liveEvents, actById, details]);

    const busy = liveCards.length >= 2;
    const quiet = liveCards.length === 1;
    const summary = useMemo(() => {
        const photos = liveCards.reduce((s, c) => s + c.detail.photos, 0);
        const on = liveCards.reduce((s, c) => s + c.detail.devOnline, 0);
        const tot = liveCards.reduce((s, c) => s + c.detail.devTotal, 0);
        return `${liveCards.length} LIVE · ${photos.toLocaleString()} PHOTOS${
            tot > 0 ? ` · ${on}/${tot} DEVICES` : ''
        }`;
    }, [liveCards]);

    const goAct = (id: number, section?: string) => {
        if (id < 0) return; /* sim cards don't navigate */
        navigate(
            `/v2/activations/${id}${section ? `/${section}` : ''}`,
        );
    };

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    const upNextSection = (
        <section>
            <div className="mb-1 flex items-baseline justify-between">
                <span className="ps-label">Up next</span>
                <button
                    type="button"
                    onClick={() => navigate('/v2/activations')}
                    className="cursor-pointer border-0 bg-transparent p-0 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-faint hover:text-foreground"
                >
                    All →
                </button>
            </div>
            {upcoming.length === 0 && (
                <p className="py-3 text-[13px] text-muted-foreground">
                    Nothing scheduled.
                </p>
            )}
            {upcoming.map((a) => {
                const d = eventDay(a.date);
                const fx = forecasts[`${a.location}|${d.format('YYYY-MM-DD')}`];
                return (
                    <div
                        key={a.id}
                        className="flex items-center gap-3.5 border-b border-line py-3.5"
                    >
                        <div
                            {...interactiveRow(() => goAct(a.id))}
                            className="min-w-0 flex-1 cursor-pointer hover:opacity-65"
                        >
                            <div className="truncate text-[14px] font-semibold">
                                {a.name}
                            </div>
                            <div className="mt-0.5 truncate text-[13px] text-faint">
                                {[a.location, d.fromNow()]
                                    .filter(Boolean)
                                    .join(' · ')}
                                {fx && (
                                    <span className="text-muted-foreground">
                                        {' '}
                                        · {fx}
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            title="Jump to media"
                            onClick={() => goAct(a.id, 'media')}
                            className="cursor-pointer border-0 bg-transparent p-1 text-faint hover:text-foreground"
                        >
                            <IconPlay />
                        </button>
                        <span className="font-mono text-[10px] font-semibold tracking-[0.08em] text-faint">
                            {dayLabel(d)}
                        </span>
                    </div>
                );
            })}
        </section>
    );

    const pipelineSection = (
        <section>
            <div className="mb-1 flex items-baseline justify-between">
                <span className="ps-label">Pipeline · Jobs</span>
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-faint">
                    {pipeline.length} open
                </span>
            </div>
            {pipeline.length === 0 && (
                <p className="py-3 text-[13px] text-muted-foreground">
                    No open jobs.
                </p>
            )}
            {pipeline.map((j) => (
                <div
                    key={j.jobId}
                    {...interactiveRow(() =>
                        navigate(`/v2/jobs/${j.jobId}`),
                    )}
                    className="flex cursor-pointer items-center gap-3.5 border-b border-line py-3.5 hover:bg-sunk"
                >
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-[14px] font-semibold">
                            {j.name}
                        </div>
                        <div className="mt-0.5 truncate text-[13px] text-faint">
                            {j.meta}
                        </div>
                    </div>
                    <span className="flex-none rounded-full border border-border px-2.5 py-[3px] font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                        {j.stage}
                    </span>
                </div>
            ))}
        </section>
    );

    const quietCard = quiet ? liveCards[0] : null;
    const quietDetail = quietCard?.detail;
    const quietRate =
        quietDetail?.rate != null ? `${quietDetail.rate}%` : '—';

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
            {/* greeting (kept from the pre-command-center home) */}
            <div className="mb-7 text-[13px] text-muted-foreground">
                {greeting}
                {weather && <> {weather}</>}
            </div>

            {/* busy: the live grid */}
            {busy && (
                <section className="mb-10">
                    <div className="mb-3.5 flex items-baseline justify-between">
                        <span className="ps-label">
                            Live now · {liveCards.length}
                        </span>
                        <span className="hidden font-mono text-[10px] font-semibold tracking-[0.1em] text-faint sm:block">
                            {summary}
                        </span>
                    </div>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(228px,1fr))] gap-3.5">
                        {liveCards.map((c) => (
                            <div
                                key={c.id}
                                className="flex flex-col rounded-[14px] border border-border px-4 pb-3 pt-3.5"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="ps-pulse ps-pulse-live !h-[7px] !w-[7px]" />
                                    <span className="font-mono text-[9px] font-semibold tracking-[0.1em] text-attention">
                                        LIVE
                                        {elapsedLabel(c.detail.firstToday)
                                            ? ` · ${elapsedLabel(
                                                  c.detail.firstToday,
                                              )}`
                                            : ''}
                                    </span>
                                    <button
                                        type="button"
                                        title="Jump to media"
                                        onClick={() => goAct(c.id, 'media')}
                                        className="ml-auto cursor-pointer border-0 bg-transparent p-0.5 text-faint hover:text-foreground"
                                    >
                                        <IconPlay size={14} />
                                    </button>
                                </div>
                                <div
                                    {...interactiveRow(() => goAct(c.id))}
                                    className="mt-2 cursor-pointer hover:opacity-65"
                                >
                                    <div className="text-[14.5px] font-bold tracking-[-0.01em]">
                                        {c.name}
                                    </div>
                                    {c.venue && (
                                        <div className="mt-0.5 truncate text-[12px] text-faint">
                                            {c.venue}
                                        </div>
                                    )}
                                </div>
                                <div className="mb-1.5 mt-2.5 font-mono text-[10px] font-semibold tracking-[0.06em] text-faint">
                                    {c.detail.photos.toLocaleString()} PH
                                    {c.detail.devTotal > 0 &&
                                        ` · ${c.detail.devOnline}/${c.detail.devTotal} DEV`}
                                </div>
                                <Sparkline
                                    series={c.detail.series}
                                    className="h-7"
                                />
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* quiet: one large command card + latest photos */}
            {quiet && quietCard && quietDetail && (
                <div className="mb-10 grid grid-cols-[repeat(auto-fit,minmax(min(380px,100%),1fr))] items-start gap-11">
                    <div>
                        <div className="rounded-[16px] border border-border p-5">
                            <div className="flex items-start gap-2.5">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="ps-pulse ps-pulse-live" />
                                        <span className="font-mono text-[10px] font-semibold tracking-[0.1em] text-attention">
                                            LIVE
                                            {elapsedLabel(
                                                quietDetail.firstToday,
                                            )
                                                ? ` · ${elapsedLabel(
                                                      quietDetail.firstToday,
                                                  )} ELAPSED`
                                                : ''}
                                        </span>
                                    </div>
                                    <div
                                        {...interactiveRow(() =>
                                            goAct(quietCard.id),
                                        )}
                                        className="mt-2 cursor-pointer text-[20px] font-bold tracking-[-0.02em] hover:opacity-65"
                                    >
                                        {quietCard.name}
                                    </div>
                                    {quietCard.venue && (
                                        <div className="mt-0.5 text-[13px] text-faint">
                                            {quietCard.venue}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    title="Open gallery"
                                    onClick={() =>
                                        goAct(quietCard.id, 'gallery')
                                    }
                                    className="cursor-pointer border-0 bg-transparent p-1 text-foreground hover:opacity-60"
                                >
                                    <IconImage />
                                </button>
                                <button
                                    type="button"
                                    title="Open overview"
                                    onClick={() => goAct(quietCard.id)}
                                    className="cursor-pointer border-0 bg-transparent p-1 text-faint hover:text-foreground"
                                >
                                    <IconSliders />
                                </button>
                            </div>
                            <div className="mt-4 grid grid-cols-3 border-t border-line">
                                {[
                                    [
                                        'Photos',
                                        (
                                            quietPhotos ??
                                            quietDetail.photos
                                        ).toLocaleString(),
                                    ],
                                    [
                                        'Devices',
                                        quietDetail.devTotal > 0
                                            ? `${quietDetail.devOnline}/${quietDetail.devTotal}`
                                            : '—',
                                    ],
                                    ['Share rate', quietRate],
                                ].map(([label, value]) => (
                                    <div key={label} className="pt-4">
                                        <div className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                                            {label}
                                        </div>
                                        <div className="mt-1 text-[19px] font-bold tracking-[-0.01em] tabular-nums">
                                            {value}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 border-t border-line pt-3.5">
                                <div className="mb-2 flex items-baseline justify-between">
                                    <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                                        Photos / hr
                                    </span>
                                    {quietDetail.firstToday && (
                                        <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint opacity-70">
                                            Since doors ·{' '}
                                            {moment(
                                                quietDetail.firstToday,
                                            ).format('h A')}
                                        </span>
                                    )}
                                </div>
                                <Sparkline
                                    series={quietDetail.series}
                                    variant="area"
                                    className="h-[52px]"
                                />
                            </div>
                        </div>
                        {quietStrip.length > 0 && (
                            <div className="mt-5">
                                <div className="ps-label mb-2">
                                    Latest photos · {quietCard.name}
                                </div>
                                <div className="flex h-[88px] gap-1.5 overflow-hidden">
                                    {quietStrip.map((p) => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            aria-label="Open in Media"
                                            onClick={() =>
                                                goAct(quietCard.id, 'media')
                                            }
                                            className="h-full flex-none cursor-pointer border-0 bg-transparent p-0"
                                        >
                                            <img
                                                src={p.uri}
                                                alt=""
                                                loading="lazy"
                                                className="h-full w-auto rounded-[6px]"
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-10">
                        {upNextSection}
                        {pipelineSection}
                    </div>
                </div>
            )}

            {/* busy or zero-live: two-column up next + pipeline */}
            {!quiet && (
                <div className="mb-10 grid grid-cols-[repeat(auto-fit,minmax(min(340px,100%),1fr))] items-start gap-11">
                    {upNextSection}
                    {pipelineSection}
                </div>
            )}
        </main>
    );
}
